import React from 'react';
import { Box, BoxProps, Breakpoint, useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { useContainerBreakpointSize } from '../../../utils/utilHooks';

export enum GridTemplateSize {
  datePicker = '14rem',
  kode = '14rem',
  id = '7rem',
  largeID = '10rem',
  doubleID = '14.5rem',
  auto = '1fr',
  iconBtn = '1.5rem'
}

export const getRepeatedGridTemplate = (min: string, max: string) =>
  `repeat(auto-fill, minmax(${min}, ${max})) auto;`;

type GridTemplateProps = {
  children: React.ReactElement[];
  gridTemplateColumns?: string;
  gridTemplateColumnsOnBreak?: string;
  gap?: string | number;
  size?: (string | { min: string; max: string })[];
  wrap?: boolean;
  breakOn?: Breakpoint;
  styleBox?: React.CSSProperties | undefined;
  breakpoints?: Partial<{ [key in Breakpoint]: number}>
};


export const InlineGridTemplate = (
  props: GridTemplateProps &
    Omit<BoxProps, 'display' | 'flexDirection' | 'flexFlow'>
) => {
  const {
    children,
    gap,
    size,
    wrap = true,
    gridTemplateColumns,
    alignSelf,
    styleBox,
    breakOn = 'xs',
    gridTemplateColumnsOnBreak,
    breakpoints,
    ...boxProps
  } = props;
  const ref = React.useRef<HTMLDivElement>(null);
  const containerSize = useContainerBreakpointSize(ref, breakpoints);

  const autoCount = size ? children.length - size.length : children.length;
  const autoGridTemplate =
    gridTemplateColumns ??
    (size
      ? [...size, ...Array(autoCount).fill('1fr')].reduce((prev, curr) => {
          if (typeof curr === 'string') return `${prev} minmax(7rem, ${curr})`;
          const current = curr as { min: string; max: string };
          if (current.max)
            return `${prev} minmax(${current.min}, ${current.max})`;
          return prev;
        }, 'repeat(auto-fit,') + ')'
      : undefined);

  const gridContainer = useGridClasses({gridTemplateColumns: autoGridTemplate, wrap, gap, breakOn, gridTemplateColumnsOnBreak, containerSize});

  return (
    <Box className={gridContainer} {...boxProps} ref={ref}>
      {children.map((nodeElement, index) => (
        <Box
          key={index}
          justifyContent="center"
          alignSelf={alignSelf ?? 'center'}
          display={'flex'}
          style={styleBox}
          flexDirection={'column'}
          height='fit-content'
        >
          {' '}
          {nodeElement}{' '}
        </Box>
      ))}
    </Box>
  );
};


const useStyles = makeStyles((theme) => ({
  gridContainer: (props: { gap?: string | number }) => ({
    display: 'grid',
    gap: props.gap ?? theme.spacing(1),
  }),
}));

const useTemplateStyles = makeStyles(() => ({
  apply: (props: { gridTemplateColumns?: string }) => ({
    gridTemplateColumns:
      props.gridTemplateColumns ?? 'repeat(auto-fit, minmax(7rem, 1fr));',
  }),
}));

const useBreakTemplateStyles = makeStyles(() => ({
  none: (props: { gridTemplateColumnsOnBreak?: string }) => ({
    gridTemplateColumns: props.gridTemplateColumnsOnBreak ?? 'none;',
  }),
}));

function useGridClasses (param: Pick<GridTemplateProps, 'gridTemplateColumns' | 'gridTemplateColumnsOnBreak' | 'gap' | 'wrap' | 'breakOn'> & {containerSize: Breakpoint}) {
  const { gridTemplateColumns, gap, gridTemplateColumnsOnBreak, containerSize, breakOn = 'xs', wrap = true } = param;
  const { breakpoints } = useTheme();
  const classes = useStyles({ gap });
  const { apply } = useTemplateStyles({ gridTemplateColumns });  
  const { none } = useBreakTemplateStyles({ gridTemplateColumnsOnBreak });
  return clsx({
    [classes.gridContainer]: true,
    [apply]: breakpoints.values[containerSize] > breakpoints.values[breakOn] || !wrap,
    [none]: breakpoints.values[containerSize] <= breakpoints.values[breakOn]  && wrap,
  });
}
